/**
 * Warm transfer actions.
 *
 * Everything they need is read from the store at call time, so the on-screen
 * row (via `useWarmTransferState`) and the keyboard shortcut run the exact same
 * flow instead of two lookalike implementations.
 */
import { useNotificationStore } from 'Modules/notification/store';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import { pushToGTMDataLayer } from 'Utils/analytics';

const logger = createLogger('warmTransfer');

/**
 * Completes the warm transfer via SIP REFER with Replaces.
 *
 * Sets the isCompletingTransfer flag, calls callManager.completeWarmTransfer,
 * clears the warm transfer context on success, fires analytics events,
 * and handles errors with notification and retry enablement.
 */
/**
 * Emit a diagnostic event without ever being able to affect the transfer.
 */
const emitWarmTransferEvent = (
  message: string,
  details: Record<string, unknown>
): void => {
  try {
    // The Call channel: these rows say what the transfer did, while the REFER
    // itself is traced off the socket by SipService.
    EventService.getInstance().logCallEvent(message, details);
  } catch (error) {
    logger.warn('Failed to record warm transfer event', {
      message,
      error: (error as Error).message,
    });
  }
};

export const completeWarmTransfer = async (): Promise<void> => {
  const {
    warmTransferContext,
    setIsCompletingTransfer,
    clearWarmTransferContext,
  } = useSoftphoneStore.getState();

  if (!warmTransferContext) {
    logger.warn('Complete warm transfer rejected: no warm transfer context');
    return;
  }

  const { originalCallId, consultationCallId, destination } =
    warmTransferContext;

  if (!consultationCallId) {
    logger.warn('Complete warm transfer rejected: no consultation call ID');
    return;
  }

  logger.info('Completing warm transfer', {
    originalCallId,
    consultationCallId,
  });

  setIsCompletingTransfer(true);

  try {
    await CallManager.getInstance().completeWarmTransfer(
      originalCallId,
      consultationCallId
    );

    // Success: clear context and fire analytics
    clearWarmTransferContext();

    logger.info('Warm transfer completed successfully', {
      originalCallId,
      consultationCallId,
    });

    pushToGTMDataLayer('warm_transfer_completed', {
      call_id: originalCallId,
      destination,
    });
    emitWarmTransferEvent('Warm transfer completed', {
      callId: consultationCallId,
      originalCallId,
      consultationCallId,
      destination,
    });
  } catch (error) {
    logger.error('Warm transfer completion failed', {
      originalCallId,
      consultationCallId,
      error: (error as Error).message,
    });

    // Fire failure analytics event
    pushToGTMDataLayer('warm_transfer_failed', {
      call_id: originalCallId,
      destination,
      error: (error as Error).message,
    });
    emitWarmTransferEvent('Warm transfer completion failed', {
      callId: consultationCallId,
      originalCallId,
      consultationCallId,
      destination,
      error: (error as Error).message,
    });

    // Show error notification
    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      message: 'Transfer failed. Please try again.',
    });

    // Re-enable the Transfer button on failure (reset completing flag)
    setIsCompletingTransfer(false);
  }
};

/**
 * Cancels the warm transfer by hanging up the consultation call.
 *
 * The `useWarmTransferState` subscription clears the context once the
 * consultation call disappears from activeCalls.
 */
export const cancelWarmTransfer = (): void => {
  const { warmTransferContext } = useSoftphoneStore.getState();

  if (!warmTransferContext?.consultationCallId) {
    logger.warn('Cancel warm transfer rejected: no consultation call ID');
    return;
  }

  logger.info('Cancelling warm transfer — hanging up consultation call', {
    consultationCallId: warmTransferContext.consultationCallId,
  });
  emitWarmTransferEvent('Warm transfer cancelled', {
    callId: warmTransferContext.consultationCallId,
    originalCallId: warmTransferContext.originalCallId,
    consultationCallId: warmTransferContext.consultationCallId,
  });

  void CallManager.getInstance().hangUp(warmTransferContext.consultationCallId);
};
